"use client";

/**
 * Renders an FAQ answer with light parsing for list-shaped content.
 * The ranking template ("Current standings: X at 16%, Y at 15%, …")
 * gets rendered as a bulleted list with the percentage in a pill;
 * everything else falls back to a plain prose paragraph. Keeps the
 * SEO copy as a single string in `seo-content-generators.ts` while
 * still letting the visible UI present the same data legibly.
 */
export function FaqAnswerBody({ answer }: { answer: string }) {
  // Detect the "Current standings: A at X%, B at Y%[, ...]. trailing prose."
  // shape emitted by `generateEventFAQs`. The leading prefix and
  // trailing prose are kept as paragraphs around the list.
  const standingsMatch = answer.match(
    /^(Current standings:\s*)([^.]+?)(\.\s+.*)?$/,
  );
  if (standingsMatch) {
    const [, prefix, body, tail] = standingsMatch;
    const items = body
      .split(/,\s*/)
      .map((seg) => seg.trim())
      .filter(Boolean)
      .map((seg) => {
        const m = seg.match(/^(.+?)\s+at\s+(\d+(?:\.\d+)?%)\s*$/i);
        if (!m) return { name: seg, value: null as string | null };
        return { name: m[1].trim(), value: m[2] };
      });
    if (items.length >= 2) {
      return (
        <div className="space-y-3">
          <p className="font-satoshi text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{prefix.replace(/:\s*$/, '').trim()}</p>
          <ol className="space-y-1.5">
            {items.map((it, i) => (
              <li
                key={i}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-[5px] bg-gray-50 dark:bg-[#0f0f12] border border-gray-200/70 dark:border-[#1A1A1A]"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-[10px] font-semibold text-gray-400 dark:text-gray-500 tabular-nums w-4 shrink-0">{i + 1}</span>
                  <span className="font-inter-tight text-sm font-medium text-gray-900 dark:text-white truncate">{it.name}</span>
                </span>
                {it.value && (
                  <span className="font-mono text-sm font-bold text-gray-400 dark:text-gray-500 tabular-nums shrink-0">{it.value}</span>
                )}
              </li>
            ))}
          </ol>
          {tail && (
            <p className="font-satoshi text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{tail.replace(/^\.\s+/, '')}</p>
          )}
        </div>
      );
    }
  }

  // Default: split on double newlines into paragraphs (none of our
  // templates use them today, but harmless if a future answer does).
  const paragraphs = answer.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return (
    <div className="space-y-2">
      {paragraphs.map((p, i) => (
        <p key={i} className="font-satoshi text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{p}</p>
      ))}
    </div>
  );
}
